"use client";

import type { AtsCheckResult, ParsedResume } from "@/lib/cv/ats-check";

// Carries the anonymous upload + computed result across the OAuth redirect, so a
// returning signed-in user resumes without re-uploading. Mirrors the approach in
// career-check-up-draft.ts (file serialized to a dataURL in localStorage).

const STORAGE_KEY = "ats-check-draft-v1";
const STORAGE_VERSION = 1;

type SerializedFile = {
  dataUrl: string;
  name: string;
  type: string;
  lastModified: number;
  size: number;
};

type StoredPayload = {
  cvFile: SerializedFile | null;
  experienceLevel: string;
  result: AtsCheckResult | null;
  parsed: ParsedResume | null;
  updatedAt: number;
};

type StoredDraft = { version: number; payload: StoredPayload };

export type AtsCheckDraft = {
  cvFile: File | null;
  experienceLevel: string;
  result: AtsCheckResult | null;
  parsed: ParsedResume | null;
};

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("Failed to read file"));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

async function serializeFile(file: File | null): Promise<SerializedFile | null> {
  if (!file) return null;
  try {
    return {
      dataUrl: await fileToDataUrl(file),
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      size: file.size,
    };
  } catch {
    return null;
  }
}

function deserializeFile(file: SerializedFile | null): File | null {
  if (!file) return null;
  try {
    const [header, base64] = file.dataUrl.split(",");
    if (!header || !base64) return null;
    const mimeMatch = header.match(/^data:(.*?);base64$/i);
    const mime = mimeMatch?.[1] || file.type || "application/pdf";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], file.name, {
      type: mime,
      lastModified: file.lastModified,
    });
  } catch {
    return null;
  }
}

export async function saveAtsCheckDraft(draft: AtsCheckDraft): Promise<void> {
  if (typeof window === "undefined") return;
  const cvFile = await serializeFile(draft.cvFile);
  const stored: StoredDraft = {
    version: STORAGE_VERSION,
    payload: {
      cvFile,
      experienceLevel: draft.experienceLevel,
      result: draft.result,
      parsed: draft.parsed,
      updatedAt: Date.now(),
    },
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Quota exceeded: drop the (large) file but keep level + result so the user
    // still sees their score on return; they re-upload before the full review.
    try {
      const withoutFile: StoredDraft = {
        ...stored,
        payload: { ...stored.payload, cvFile: null },
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutFile));
    } catch {
      // give up silently
    }
  }
}

export function loadAtsCheckDraft(): AtsCheckDraft | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    if (parsed.version !== STORAGE_VERSION || !parsed.payload) return null;
    const payload = parsed.payload;
    return {
      cvFile: deserializeFile(payload.cvFile ?? null),
      experienceLevel:
        typeof payload.experienceLevel === "string"
          ? payload.experienceLevel
          : "",
      result: (payload.result as AtsCheckResult | null) ?? null,
      parsed: (payload.parsed as ParsedResume | null) ?? null,
    };
  } catch {
    return null;
  }
}

export function clearAtsCheckDraft() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}
